import PropTypes from "prop-types";
import React, { createContext } from "react";
import { MultiplayerProvider } from "./MultiplayerContext";
import { useUserContext } from "./UserContext";
import { useWebSocket } from "./hooks/useWebSocket";

// Create a context to provide the WebSocket connection to other components
const WebSocketContext = createContext(null);

/**
 * WebSocketProvider - Manages multiplayer WebSocket connection
 * 
 * Authentication Flow:
 * 1. User logs in via REST API: POST /api/auth/login
 *    - Returns: { token, user: { id, username, email, ... } }
 * 2. Token and user data stored in localStorage by EmailLoginDialog
 * 3. UserContext reads from localStorage and provides user data
 * 4. WebSocket connects using: ws://host/multiplayer/{userId}/{username}
 * 
 * The WebSocket connection format includes user credentials in the URL path,
 * so no separate login step is needed at the WebSocket level.
 */
export function WebSocketProvider({ children }) {
  const { username, userId, user, loading } = useUserContext();

  // Only connect WebSocket if user is logged in (has userId and username)
  // Get userId from user object if userId prop is undefined
  const actualUserId = userId || user?.id;
  const actualUsername = username || user?.username;

  // Only create WebSocket URL if we have both userId and username and not loading
  // Backend expects: ws://host/multiplayer/{userId}/{username}
  // This format includes user credentials in the URL path (path-based authentication)
  // 
  // Environment variable handling:
  // - Local: ws://localhost:3001
  // - Production: wss://crossovergames-8b4eb8e6663f.herokuapp.com
  const getWebSocketURL = () => {
    const wsBase = process.env.REACT_APP_WS_URL || 'wss://crossovergames-8b4eb8e6663f.herokuapp.com';
    // Ensure protocol matches (ws for local, wss for production)
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const protocol = wsBase.startsWith('ws://') || wsBase.startsWith('wss://') 
      ? '' 
      : (isLocal ? 'ws://' : 'wss://');
    const baseUrl = wsBase.startsWith('ws://') || wsBase.startsWith('wss://') 
      ? wsBase 
      : `${protocol}${wsBase}`;
    return `${baseUrl}/multiplayer/${actualUserId}/${actualUsername}`;
  };

  const wsURL = (!loading && actualUserId && actualUsername) 
    ? getWebSocketURL()
    : null;

  const socket = useWebSocket(wsURL);

  return (
    <WebSocketContext.Provider value={socket}>
      <MultiplayerProvider>{children}</MultiplayerProvider>
    </WebSocketContext.Provider>
  );
}

WebSocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useWebSocketContext = () => {
  const context = React.useContext(WebSocketContext);

  return context;
};
